import React from 'react';

/**
 * Generic suggestion item interface that can be extended by features
 */
export interface AutocompleteSuggestionItem {
  /** The suggestion text to display and select */
  suggestion: string;
  /** Additional data that features can use for their specific needs */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

/**
 * Configuration for the generic useAutocomplete hook
 */
export interface UseAutocompleteProps<T extends AutocompleteSuggestionItem> {
  /** Function to fetch filtered suggestions based on input value */
  fetchSuggestions: (inputValue: string) => Promise<T[]>;
  /** Optional function to fetch all available suggestions (e.g., for focus/blur events) */
  fetchAllSuggestions?: () => Promise<T[]>;
  /** Callback when user selects a suggestion */
  onSuggestionSelect: (suggestion: T) => void;
  /** Optional validation function to determine when suggestions should be fetched */
  isValid?: () => boolean;
  /** Debounce delay in milliseconds for API calls. Defaults to 500ms */
  debounceMs?: number;
  /** Blur delay in milliseconds to allow clicks before hiding. Defaults to 150ms */
  blurDelayMs?: number;
}

/**
 * Generic autocomplete hook that provides all UI interaction logic.
 *
 * This hook handles:
 * - Suggestion state management
 * - Debounced API calls
 * - Keyboard navigation (Arrow Up/Down, Enter, Escape)
 * - Focus/blur behavior with click-to-select support
 * - Loading states and error handling
 * - Dropdown restoration with Down arrow
 * - Automatic cleanup
 *
 * Features can use this as a foundation and layer on their specific API calls
 * and business logic.
 *
 * @param config - Configuration object for autocomplete behavior
 * @returns State and handlers for autocomplete functionality
 */
export function useAutocomplete<T extends AutocompleteSuggestionItem>({
  fetchSuggestions,
  fetchAllSuggestions,
  onSuggestionSelect,
  isValid,
  debounceMs = 500,
  blurDelayMs = 150,
}: UseAutocompleteProps<T>) {
  // State for managing the suggestion dropdown
  const [suggestions, setSuggestions] = React.useState<T[]>([]);
  const [isFetching, setIsFetching] = React.useState(false);
  const [focusedIndex, setFocusedIndex] = React.useState(-1);

  // Timeout refs for managing debounced API calls and delayed blur handling
  const debounceTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const blurTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  /**
   * Internal function to fetch suggestions with error handling
   */
  const fetchSuggestionsInternal = React.useCallback(
    async (inputValue: string) => {
      // Early return if validation fails
      if (isValid && !isValid()) {
        setSuggestions([]);
        return;
      }

      setIsFetching(true);
      try {
        const results = await fetchSuggestions(inputValue);
        setSuggestions(results || []);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (_) {
        // Silently handle errors by clearing suggestions
        setSuggestions([]);
      } finally {
        setIsFetching(false);
      }
    },
    [fetchSuggestions, isValid]
  );

  /**
   * Internal function to fetch all suggestions with error handling
   */
  const fetchAllSuggestionsInternal = React.useCallback(async () => {
    // Early return if validation fails or no fetch function provided
    if ((isValid && !isValid()) || !fetchAllSuggestions) {
      setSuggestions([]);
      return;
    }

    setIsFetching(true);
    try {
      const results = await fetchAllSuggestions();
      setSuggestions(results || []);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_) {
      // Silently handle errors by clearing suggestions
      setSuggestions([]);
    } finally {
      setIsFetching(false);
    }
  }, [fetchAllSuggestions, isValid]);

  /**
   * Handles input value changes with debouncing to prevent excessive API calls.
   */
  const handleInputChange = React.useCallback(
    (value: string) => {
      // Reset focused index when input changes
      setFocusedIndex(-1);

      // Clear any pending blur timeout (user is actively typing)
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }

      // Only make API calls if validation passes (or no validation provided)
      if (!isValid || isValid()) {
        // Clear existing debounce timeout
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }

        // If input is empty and we have fetchAllSuggestions, use that
        if (value.trim() === '' && fetchAllSuggestions) {
          debounceTimeoutRef.current = setTimeout(() => {
            void fetchAllSuggestionsInternal();
          }, debounceMs);
        } else if (value.trim() !== '') {
          // If input has content, filter suggestions
          debounceTimeoutRef.current = setTimeout(() => {
            void fetchSuggestionsInternal(value);
          }, debounceMs);
        }
      } else {
        // Clear suggestions if validation fails
        setSuggestions([]);
      }
    },
    [
      fetchSuggestionsInternal,
      fetchAllSuggestionsInternal,
      debounceMs,
      isValid,
      fetchAllSuggestions,
    ]
  );

  /**
   * Handles input blur events with a delay to allow clicks on suggestions.
   */
  const handleInputBlur = React.useCallback(
    (currentValue: string) => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }

      // Small delay to allow suggestion clicks to register before hiding
      blurTimeoutRef.current = setTimeout(() => {
        // If input is empty, close the dropdown
        if (currentValue.trim() === '') {
          setSuggestions([]);
          setFocusedIndex(-1);
        }
        // If input has content, keep suggestions open (they might click back in to continue editing)
      }, blurDelayMs);
    },
    [blurDelayMs]
  );

  /**
   * Handles input focus events to reopen dropdown if suggestions should be available.
   */
  const handleInputFocus = React.useCallback(
    async (currentValue: string) => {
      // Clear any pending blur timeout
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }

      // If validation passes and we don't already have suggestions, fetch them
      if ((!isValid || isValid()) && suggestions.length === 0) {
        if (currentValue.trim() === '' && fetchAllSuggestions) {
          // Empty input - show all suggestions
          await fetchAllSuggestionsInternal();
        } else if (currentValue.trim() !== '') {
          // Input has content - filter suggestions
          await fetchSuggestionsInternal(currentValue);
        }
      }
    },
    [
      isValid,
      suggestions.length,
      fetchAllSuggestionsInternal,
      fetchSuggestionsInternal,
      fetchAllSuggestions,
    ]
  );

  /**
   * Handles keyboard navigation within the suggestion dropdown.
   */
  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>, currentValue: string) => {
      // Special case: Down arrow when no suggestions visible
      // This allows users to restore the dropdown after pressing escape
      if (e.key === 'ArrowDown' && suggestions.length === 0) {
        if (!isValid || isValid()) {
          e.preventDefault();
          if (currentValue.trim() === '' && fetchAllSuggestions) {
            // Empty input - show all suggestions
            void fetchAllSuggestionsInternal();
          } else if (currentValue.trim() !== '') {
            // Input has content - filter suggestions
            void fetchSuggestionsInternal(currentValue);
          }
          return;
        }
      }

      // Only handle navigation if suggestions are visible
      if (suggestions.length === 0) {
        return;
      }

      // Handle specific navigation keys
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedIndex((prevIndex) =>
          prevIndex === suggestions.length - 1 ? 0 : prevIndex + 1
        );
        return;
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedIndex((prevIndex) =>
          prevIndex <= 0 ? suggestions.length - 1 : prevIndex - 1
        );
        return;
      }

      if (
        e.key === 'Enter' &&
        focusedIndex >= 0 &&
        focusedIndex < suggestions.length
      ) {
        e.preventDefault();
        const selectedSuggestion = suggestions[focusedIndex];
        onSuggestionSelect(selectedSuggestion);
        setSuggestions([]);
        setFocusedIndex(-1);

        if (blurTimeoutRef.current) {
          clearTimeout(blurTimeoutRef.current);
          blurTimeoutRef.current = null;
        }
        return;
      }

      if (
        e.key === 'Enter' &&
        focusedIndex === -1 &&
        suggestions.length > 0 &&
        currentValue.trim() !== ''
      ) {
        // User typed complete entry and pressed Enter without using arrow keys
        // Check if there's an exact match and close dropdown
        const exactMatch = suggestions.find(
          (suggestion) =>
            suggestion.suggestion.toLowerCase() === currentValue.toLowerCase()
        );

        if (exactMatch) {
          onSuggestionSelect(exactMatch);
        }

        // Close dropdown regardless of whether there was an exact match
        setSuggestions([]);
        setFocusedIndex(-1);

        if (blurTimeoutRef.current) {
          clearTimeout(blurTimeoutRef.current);
          blurTimeoutRef.current = null;
        }
        return;
      }

      if (e.key === 'Escape' && suggestions.length > 0) {
        e.preventDefault();
        setSuggestions([]);
        setFocusedIndex(-1);
        if (blurTimeoutRef.current) {
          clearTimeout(blurTimeoutRef.current);
          blurTimeoutRef.current = null;
        }
        return;
      }
    },
    [
      suggestions,
      focusedIndex,
      onSuggestionSelect,
      isValid,
      fetchAllSuggestionsInternal,
      fetchSuggestionsInternal,
      fetchAllSuggestions,
    ]
  );

  /**
   * Handles click selection of a suggestion.
   */
  const handleSuggestionClick = React.useCallback(
    (suggestion: T) => {
      onSuggestionSelect(suggestion);
      setSuggestions([]);
      setFocusedIndex(-1);

      // Clear any pending blur timeout
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }
    },
    [onSuggestionSelect]
  );

  /**
   * Manually clears all suggestions and resets state.
   */
  const clearSuggestions = React.useCallback(() => {
    setSuggestions([]);
    setFocusedIndex(-1);

    // Clear all pending timeouts
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
  }, []);

  // Cleanup effect: clear timeouts on unmount to prevent memory leaks
  React.useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return {
    /** Array of suggestions to display */
    suggestions,
    /** Whether an API call is currently in progress */
    isFetching,
    /** Index of the currently focused suggestion (-1 if none) */
    focusedIndex,
    /** Handler for input value changes (includes debouncing logic) */
    handleInputChange,
    /** Handler for input blur events (includes delay for click handling) */
    handleInputBlur,
    /** Handler for input focus events to reopen dropdown */
    handleInputFocus,
    /** Handler for keyboard navigation that doesn't block typing */
    handleKeyDown,
    /** Handler for suggestion clicks */
    handleSuggestionClick,
    /** Function to manually clear all suggestions */
    clearSuggestions,
    /** Function to manually fetch all suggestions */
    fetchAllSuggestions: fetchAllSuggestionsInternal,
  };
}
